import {inject, injectable} from 'inversify';

import type {
  CoreCoachGoalsUnboundCreateParams,
  CoreCoachSubgoalsUnboundCreateParams,
} from '../../../../../api/types/core/methods';

import {goalStore} from '../../../../../api/goal';
import {GoalMapperJrpc} from '../../../../domain/Goal/mappers/Goal.mapper.jrpc';
import {SubgoalMapperJrpc} from '../../../../domain/Subgoal/mappers/Subgoal.mapper.jrpc';
import {CreateGoalsInCourseCmd} from '../CreateGoalsInCourse.cmd';

@injectable()
export class CreateGoalsInCourseCmdJrpc implements CreateGoalsInCourseCmd {
  constructor(
    @inject(GoalMapperJrpc)
    private readonly goalMapper: GoalMapperJrpc,
    @inject(SubgoalMapperJrpc)
    private readonly subgoalMapper: SubgoalMapperJrpc,
  ) {}

  async exec({
    courseId,
    goals,
    programId,
  }: CreateGoalsInCourseCmd.Props): Promise<CreateGoalsInCourseCmd.Response> {
    const goalExpand = this.goalMapper.expand.getByEnv();
    const subgoalExpand = this.subgoalMapper.expand.getByEnv();

    const goalBatchParams: CoreCoachGoalsUnboundCreateParams[] = goals.map(
      (goal, index) => ({
        course_id: courseId,
        description: goal.description,
        expand: goalExpand,
        is_client_managed: goal.isClientManaged,
        parent_goal_id: goal.id.includes('goal') ? goal.id : null,
        program_id: programId,
        sort_order: index * 10,
        title: goal.title,
      }),
    );

    const subgoalBatchParams = goals.reduce<
      CoreCoachSubgoalsUnboundCreateParams[]
    >(
      (acc, goal) => [
        ...acc,
        ...goal.subgoals.map((subgoal, index) => ({
          checkpoints: subgoal.checkpoints.map(({title}) => title),
          color: subgoal.color,
          description: subgoal.description,
          end_date: subgoal.endDate!.toISOString(),
          expand: subgoalExpand,
          goal_id: goal.id,
          icon: subgoal.iconName,
          initial_value: subgoal.initialValue,
          sort_order: index * 10,
          start_date: subgoal.startDate!.toISOString(),
          target_value: subgoal.targetValue,
          title: subgoal.title,
          unit: subgoal.unit,
        })),
      ],
      [],
    );

    const createdGoals = (await goalStore.createBatch(
      goalBatchParams,
    )) as GoalMapperJrpc.ExpandedApiData[];
    const goalModels = createdGoals.map(goal =>
      this.goalMapper.mapDataToModel(goal),
    );
    const goalsByParentId = new Map(
      goalModels.map(goal => [goal.parentGoalId, goal]),
    );
    const goalsById = new Map(goalModels.map(goal => [goal.id, goal]));

    if (subgoalBatchParams.length) {
      const params = subgoalBatchParams.map(subgoal => ({
        ...subgoal,
        goal_id: goalsByParentId.get(subgoal.goal_id)!.id,
      }));

      const createdSubgoals = (await goalStore.sub.createBatch(
        params,
      )) as SubgoalMapperJrpc.ExpandedApiData[];

      createdSubgoals.forEach(subgoal => {
        goalsById
          .get(subgoal.goal_id)
          ?.subgoals.push(this.subgoalMapper.mapDataToModel(subgoal));
      });
    }

    return goalModels;
  }
}
